import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProjectionScenario } from '../../../models/projection.model';

@Component({
  selector: 'th[appProjectionScenarioHeader]',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="scenario-name-row">
      <span class="scenario-name">{{scenario.name}}</span>
      <a *ngIf="scenario.type === 'scenario'" class="delete-scenario-btn"
         (click)="deleteScenario.emit(scenario.id)" title="Delete Scenario">
        <i class="material-icons" style="font-size: 16px;">close</i>
      </a>
    </div>
    <div class="scenario-year">{{scenarioYear}}</div>
    <div class="scenario-summary">
      <div class="summary-item">
        <span class="summary-label">Taxable Income</span>
        <span class="summary-value">{{formatHeaderCurrency(scenario.taxableIncome)}}</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">Total Tax</span>
        <span class="summary-value total-tax">{{formatHeaderCurrency(scenario.totalTax)}}</span>
      </div>
      <ng-container *ngIf="hasAppliedStrategies()">
        <div class="summary-item">
          <span class="summary-label">Strategy Savings</span>
          <span class="summary-value strategy-savings">-{{formatHeaderCurrency(getTotalStrategySavings())}}</span>
        </div>
        <div class="summary-item projected-tax-item">
          <span class="summary-label projected-tax-label">Projected Tax</span>
          <span class="summary-value projected-tax-value">{{formatHeaderCurrency(getProjectedTax())}}</span>
        </div>
      </ng-container>
      <div class="summary-item delta-item" *ngIf="baselineDelta !== null">
        <span class="summary-label">vs. Baseline</span>
        <span class="summary-value delta-value"
              [ngClass]="{'delta-favorable': isDeltaFavorable(), 'delta-unfavorable': !isDeltaFavorable()}">
          {{formatDelta(getAdjustedBaselineDelta())}}
        </span>
      </div>
    </div>
    <div class="strategy-actions" *ngIf="scenario.type === 'scenario'">
      <ng-container *ngIf="hasAppliedStrategies()">
        <a class="strategy-link clear-link" (click)="clearStrategies.emit(scenario.id)">Clear Strategies</a>
      </ng-container>
      <a class="strategy-link" *ngIf="!hasAppliedStrategies()" (click)="applyStrategies.emit(scenario.id)">
        Apply Plan or Strategies...
      </a>
    </div>
  `,
  styles: [`
    .scenario-name-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 2px;
    }

    .scenario-name {
      font-size: 13px;
      font-weight: 600;
      color: #333;
    }

    .delete-scenario-btn {
      color: #999;
      cursor: pointer;
      opacity: 0.5;
    }
    .delete-scenario-btn:hover {
      color: #c0392b;
      opacity: 1;
    }

    .scenario-year {
      font-size: 11px;
      color: #666;
      margin-bottom: 8px;
    }

    .summary-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4px;
    }

    .summary-label {
      font-size: 11px;
      color: #666;
    }

    .summary-value {
      font-size: 13px;
      font-weight: 600;
      color: #333;
    }
    .summary-value.total-tax { color: #c0392b; }
    .summary-value.delta-value { font-size: 12px; }
    .summary-value.delta-favorable { color: #27ae60; }
    .summary-value.delta-unfavorable { color: #c0392b; }

    .delta-item {
      margin-top: 2px;
      border-top: 1px solid rgba(0, 0, 0, 0.08);
      padding-top: 2px;
    }

    .strategy-actions {
      margin-top: 4px;
      font-size: 11px;
    }

    .strategy-link {
      color: #1570EF;
      cursor: pointer;
      text-decoration: none;
      font-size: 11px;
    }
    .strategy-link:hover { text-decoration: underline; }
    .strategy-link.clear-link { color: #c0392b; }

    .summary-value.strategy-savings { color: #27ae60; font-size: 12px; }

    .projected-tax-item {
      border-top: 1px solid rgba(0, 0, 0, 0.12);
      padding-top: 3px;
      margin-top: 2px;
    }
    .projected-tax-label { font-weight: 600; color: #333; }
    .projected-tax-value { color: #1570EF; }
  `]
})
export class ProjectionScenarioHeaderComponent {
  @Input() scenario: ProjectionScenario;
  @Input() scenarioYear: number;
  @Input() baselineDelta: number | null = null;

  @Output() deleteScenario = new EventEmitter<string>();
  @Output() applyStrategies = new EventEmitter<string>();
  @Output() clearStrategies = new EventEmitter<string>();

  formatHeaderCurrency(value: number): string {
    if (value == null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  formatDelta(delta: number): string {
    if (delta === 0) return '$0';
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.abs(delta));
    return delta < 0 ? '-' + formatted : '+' + formatted;
  }

  getProjectedTax(): number {
    return Math.max(0, (this.scenario?.totalTax || 0) - this.getTotalStrategySavings());
  }

  getAdjustedBaselineDelta(): number {
    if (this.baselineDelta == null) return 0;
    if (this.hasAppliedStrategies()) {
      return this.getProjectedTax() - ((this.scenario?.totalTax || 0) - this.baselineDelta);
    }
    return this.baselineDelta;
  }

  isDeltaFavorable(): boolean {
    const delta = this.hasAppliedStrategies() ? this.getAdjustedBaselineDelta() : this.baselineDelta;
    return delta != null && delta < 0;
  }

  hasAppliedStrategies(): boolean {
    return this.scenario?.appliedStrategies && this.scenario.appliedStrategies.length > 0;
  }

  getTotalStrategySavings(): number {
    if (!this.scenario?.appliedStrategies || this.scenario.appliedStrategies.length === 0) return 0;
    return this.scenario.appliedStrategies.reduce((sum, s) => sum + (s.adjustment || 0), 0);
  }
}
